import { Link } from 'react-router-dom';
import { Icon, Tone } from '~/components/ui';
import type { CandidateInvitation } from '../api/threadsClient';
import { useLocale, useT } from '~/i18n';
import { absoluteTime, isPendingInvitation, relativeTime } from '../lib/threads';

interface Props {
  invitation: CandidateInvitation;
}

/**
 * Una invitación de agencia dentro del mismo inbox.
 *
 * Comparte la fila con los hilos porque para el candidato son la misma cosa —
 * alguien del otro lado quiere hablarle— pero tiene que distinguirse de un
 * correo: otro glifo, el rótulo "Invitación" y su propio destino, que sigue
 * siendo `/invitations/:matchId` (la pantalla que ya existe y a la que apunta el
 * correo). No se duplica el flujo de aceptar/rechazar acá.
 */
export default function InvitationListItem({ invitation }: Props) {
  const ws = useT('workspace');
  const t = ws.invitationItem;
  const locale = useLocale();
  const pending = isPendingInvitation(invitation);
  const at = invitation.responded_at ?? invitation.invited_at ?? '';

  return (
    <li className="inbox__row">
      <Link
        to={`/invitations/${invitation._id}`}
        className="inbox__link"
        data-unread={pending ? 'true' : 'false'}
      >
        <span className="inbox__art" aria-hidden="true">
          <Icon name="users" size={18} />
        </span>

        <span className="inbox__main">
          <span className="inbox__title">
            <strong>{invitation.agency.name}</strong>
            <span className="hush">{invitation.position.title}</span>
            <span className="brand-label">{t.label}</span>
          </span>
          {invitation.position.location && (
            <span className="inbox__where">{invitation.position.location}</span>
          )}
          <span className="inbox__preview">
            {pending
              ? t.pendingPreview
              : invitation.status === 'candidate_accepted'
                ? t.sharedWith(invitation.agency.name)
                : ws.invitation.notShared}
          </span>
        </span>

        <span className="inbox__side">
          <time className="inbox__time" dateTime={at} title={absoluteTime(at, locale)}>
            {relativeTime(at, undefined, locale)}
          </time>
          {pending ? (
            <Tone tone="warn">{t.unanswered}</Tone>
          ) : (
            <Tone tone={invitation.status === 'candidate_accepted' ? 'good' : 'muted'}>
              {invitation.status === 'candidate_accepted' ? ws.invitation.accepted : ws.invitation.declined}
            </Tone>
          )}
        </span>
      </Link>
    </li>
  );
}
